import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from "react";
import { Puck } from "@puckeditor/core";
import type { Overrides, Viewports } from "@puckeditor/core";
import "@puckeditor/core/puck.css";
import type { ConsentTemplate } from "../../../api/models/consentModels";
import ConfirmModal from "@source/components/confirmModal/confirmModal";
import Svg from "@source/components/UI/svg/svg";
import iconArchive from "@source/assets/icons/iconArchive.svg";
import iconCopy from "@source/assets/icons/iconCopy.svg";
import { TemplateBuilderProvider, useTemplateBuilder, useTemplateDataChange } from "./context";
import { createTemplateBuilderConfig } from "./registry";
import { stylesForEditor, stylesFromEditor } from "./serialization";
import { hydrateTemplate } from "./serialization";
import type { TemplateBuilderState } from "./state";

type TemplateBuilderActions = {
  onCopy?: () => Promise<void>;
  onArchive?: () => Promise<void>;
  copyDisabled?: boolean;
  copyLoading?: boolean;
  archiveDisabled?: boolean;
  archiveLoading?: boolean;
  title: string;
  editable: boolean;
  titleDisabled?: boolean;
  onTitleChange?: (title: string) => void;
};

const TemplateBuilderActionsContext = createContext<TemplateBuilderActions>({title: "", editable: false});

// In Puck 0.23, `children` is always its Publish button (even without an
// `onPublish` callback), so intentionally do not render it.
const HeaderActions = () => {
  const {onCopy, onArchive, copyDisabled, copyLoading, archiveDisabled, archiveLoading} = useContext(TemplateBuilderActionsContext);
  return <>
    {onCopy ? <button
      type="button"
      className="template-builder-header-action"
      onClick={onCopy}
      disabled={copyDisabled}
      aria-label="Copy template"
      title="Copy template"
      aria-busy={copyLoading || undefined}
    >
      <span aria-hidden="true"><Svg src={iconCopy} color="#3956C7" width={18} height={18}/></span>
    </button> : null}
    {onArchive ? <ConfirmModal
      title="Archive template?"
      subTitle="Archived templates remain read-only and cannot be selected by Consent Categories."
      confirmCb={onArchive}
      confirmLoading={archiveLoading}
      confirmBtnInner="Archive template"
      rejectBtnInner="Cancel"
      icon={iconArchive}
      hideIcon
    >
      <button
        type="button"
        className="template-builder-header-action"
        disabled={archiveDisabled}
        aria-label="Archive template"
        title="Archive template"
        aria-busy={archiveLoading || undefined}
      >
        <span aria-hidden="true"><Svg src={iconArchive} color="#7E808F" width={18} height={18}/></span>
      </button>
    </ConfirmModal> : null}
  </>;
};

const HeaderTitle = () => {
  const {title, editable, titleDisabled, onTitleChange} = useContext(TemplateBuilderActionsContext);
  if (!editable) return <span className="template-builder-header-title">{title || "Untitled template"}</span>;
  return <input
    className="template-builder-header-title-input"
    aria-label="Template title"
    value={title}
    required
    disabled={titleDisabled}
    onChange={(event) => onTitleChange?.(event.target.value)}
  />;
};

// Puck 0.23 exposes the header as an override, but its `headerTitle` prop is
// display-only. Keep Puck's own header/sidebar controls and replace only the
// title region with the page-owned, editable title field.
const BuilderHeader = ({children}: {children: ReactNode}) => <div className="template-builder-puck-header">
  {children}
  <div className="template-builder-puck-header-title"><HeaderTitle /></div>
</div>;

// Keep Puck's override identity stable. Puck remounts override components when
// their function changes, which can otherwise reset measured canvas state.
const puckOverrides: Partial<Overrides> = {
  header: BuilderHeader,
  headerActions: HeaderActions,
};

// Puck's default Large (1280px) viewport calculates an Auto zoom of 66% in
// the builder's three-column layout. Start at the stable full-width canvas and
// retain the useful narrow device presets and regular zoom controls.
const builderViewports: Viewports = [
  { width: 360, height: "auto", icon: "Smartphone", label: "Small" },
  { width: 768, height: "auto", icon: "Tablet", label: "Medium" },
  { width: "100%", height: "auto", icon: "Monitor", label: "Full-width" },
];

const builderUi = {
  viewports: {
    current: { width: "100%" as const, height: "auto" as const },
    controlsVisible: true,
    options: [],
  },
};

const EditorSurface = ({
  onStateChange,
  actions,
}: {
  onStateChange?: (state: TemplateBuilderState, payload: ReturnType<typeof import("./serialization").buildTemplatePayload>, validationIssues: string[]) => void;
  actions: TemplateBuilderActions;
}) => {
  const { data, inputDefinitions, payload, dispatch, puckRevision, validationIssues } = useTemplateBuilder();
  const replaceFromPuck = useTemplateDataChange();
  const onChange = useCallback((next: typeof data) => replaceFromPuck(stylesFromEditor(next)), [replaceFromPuck]);
  const puckData = useMemo(() => stylesForEditor(data), [data]);
  const ensureInsertedInput = useCallback((actor: "employee" | "family", inputId: string, kind: import("./types").InputKind) => {
    dispatch({ type: "ensureInsertedInput", actor, inputId, kind });
  }, [dispatch]);
  const config = useMemo(() => createTemplateBuilderConfig(ensureInsertedInput), [ensureInsertedInput]);
  useEffect(() => {
    onStateChange?.({data, inputDefinitions}, payload, validationIssues);
  }, [data, inputDefinitions, onStateChange, payload, validationIssues]);
  return <TemplateBuilderActionsContext.Provider value={actions}>
    <section className="template-builder-editor" aria-label="Template visual builder">
    {validationIssues.length > 0 && <section role="alert" className="template-builder-validation">
      <strong>Fix these template issues before saving:</strong>
      <ul>{validationIssues.map((issue) => <li key={issue}>{issue}</li>)}</ul>
    </section>}
    <Puck
      key={puckRevision}
      config={config}
      data={puckData}
      onChange={onChange}
      overrides={puckOverrides}
      headerTitle={actions.title || "Untitled template"}
      ui={builderUi}
      viewports={builderViewports}
    />
    </section>
  </TemplateBuilderActionsContext.Provider>;
};

/**
 * Editor-only composition shell. Page routing, API mutation, and product actions
 * deliberately remain with the later page orchestration layer.
 */
export const TemplateBuilderEditor = ({
  template,
  initialState,
  onStateChange,
  onCopy,
  onArchive,
  copyDisabled,
  copyLoading,
  archiveDisabled,
  archiveLoading,
  title,
  editable = true,
  titleDisabled,
  onTitleChange,
}: {
  template?: Pick<ConsentTemplate, "structure" | "employeeInputs" | "familyMemberInputs">;
  initialState?: TemplateBuilderState;
  onStateChange?: (state: TemplateBuilderState, payload: ReturnType<typeof import("./serialization").buildTemplatePayload>, validationIssues: string[]) => void;
  onCopy?: () => Promise<void>;
  onArchive?: () => Promise<void>;
  copyDisabled?: boolean;
  copyLoading?: boolean;
  archiveDisabled?: boolean;
  archiveLoading?: boolean;
  title: string;
  editable?: boolean;
  titleDisabled?: boolean;
  onTitleChange?: (title: string) => void;
}) => (
  <TemplateBuilderProvider initialState={initialState ?? hydrateTemplate(template)}>
    <EditorSurface
      onStateChange={onStateChange}
      actions={{onCopy, onArchive, copyDisabled, copyLoading, archiveDisabled, archiveLoading, title, editable, titleDisabled, onTitleChange}}
    />
  </TemplateBuilderProvider>
);
